import { useVirtualizer } from '@tanstack/react-virtual';
import format from 'date-fns/format';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
  REPORTING_NONE_KEY,
  ReportingGrain,
  ReportingGroupLevel,
  WeeklyAdoptionLevelRow,
} from '@eluve/api-contract';
import {
  Button,
  Icon,
  LoadingIcon,
  Progress,
  Stack,
  Text,
} from '@eluve/components';

import { UserCountBadge, UserDetailsHover } from './UserRowExtras';
import { formatSeconds, latencyBreakdownTitle } from './latency';

/** Which measure the bucket cells render. */
export type TableCellMode = 'adoption' | 'latency';

/** Ancestor constraints accumulated while expanding down the tree. */
export type ParentScope = {
  parentLocationId?: string;
  parentSpecialty?: string;
  parentMicrophone?: string;
  parentTemplateId?: string;
};

export type ChildrenState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'loaded'; rows: WeeklyAdoptionLevelRow[] };

/** Column sort: the name column or one bucket column (by its start date). */
export type TableSort = { key: 'name' | string; dir: 'asc' | 'desc' };

const FIRST_COL_PX = 360;
const BUCKET_COL_PX = 168;
const ESTIMATED_ROW_PX = 56;

const meterColor = (pct: number) => {
  if (pct >= 70) {
    return 'bg-bg-positive-strong-primary';
  }
  if (pct >= 55) {
    return 'bg-bg-warning-strong-primary';
  }
  return 'bg-bg-negative-strong-primary';
};

const PctRow: React.FC<{ pct: number | null; label: string }> = ({
  pct,
  label,
}) =>
  pct == null ? null : (
    <div className="flex items-center gap-2" title={label}>
      <Progress
        className="h-1.5 w-14"
        value={pct}
        indicatorClassName={meterColor(pct)}
      />
      <Text size="xs" className="tabular-nums">
        {pct}%
      </Text>
    </div>
  );

/**
 * Signed/completed/all counts with the two headline ratios — adoption
 * (completed / all) over note acceptance (signed / completed). Falls back
 * to the server's legacy pair when the cell predates raw counts.
 */
const MetricCell: React.FC<{
  cell: WeeklyAdoptionLevelRow['cells'][number];
}> = ({ cell }) => {
  const { counts } = cell;
  if (!counts) {
    const { numerator, denominator, pct } = cell;
    return pct == null ? (
      <Text color="tertiary">—</Text>
    ) : (
      <Text size="xs" color="tertiary" className="tabular-nums">
        {numerator}/{denominator} · {pct}%
      </Text>
    );
  }
  if (counts.total === 0) {
    return <Text color="tertiary">—</Text>;
  }
  const completion = Math.round((counts.completed / counts.total) * 100);
  const signedPct =
    counts.completed > 0
      ? Math.round((counts.signed / counts.completed) * 100)
      : null;
  return (
    <div className="flex flex-col gap-1">
      <div title="Signed / Completed / Scheduled appointments">
        <Text
          size="xs"
          color="tertiary"
          className="whitespace-nowrap tabular-nums"
        >
          {counts.signed}/{counts.completed}/{counts.total}
        </Text>
      </div>
      <PctRow pct={completion} label="Completion % — Completed / Scheduled" />
      <PctRow pct={signedPct} label="Note signed % — Signed / Completed" />
    </div>
  );
};

/** Note-wait distribution for one bucket: how many notes, p50/p90 wait. */
const LatencyCell: React.FC<{
  cell: WeeklyAdoptionLevelRow['cells'][number];
}> = ({ cell }) => {
  const { latency } = cell;
  if (!latency || latency.noteWait.n === 0) {
    return <Text color="tertiary">—</Text>;
  }
  return (
    <div
      className="flex flex-col gap-0.5"
      title={latencyBreakdownTitle(latency)}
    >
      <Text size="xs" color="tertiary" className="tabular-nums">
        {latency.noteWait.n} {latency.noteWait.n === 1 ? 'appt' : 'appts'}
      </Text>
      <Text size="xs" className="whitespace-nowrap tabular-nums">
        p50 {formatSeconds(latency.noteWait.p50)} · p99{' '}
        {formatSeconds(latency.noteWait.p99)}
      </Text>
    </div>
  );
};

type FlatRow =
  | {
      kind: 'data';
      row: WeeklyAdoptionLevelRow;
      depth: number;
      nodeId: string;
      isLeaf: boolean;
      isExpanded: boolean;
      childScope: ParentScope;
    }
  | { kind: 'loading' | 'error'; nodeId: string; depth: number };

const flatRowKey = (flat: FlatRow) =>
  flat.kind === 'data' ? flat.nodeId : `${flat.nodeId}:${flat.kind}`;

/** Name + sublabel block shared by both tables' first column. */
export const RowLabelBlock: React.FC<{
  label: string;
  sublabel: string | null;
  isLeaf: boolean;
}> = ({ label, sublabel, isLeaf }) => (
  <Stack gap={0.5} fullWidth={false} className="min-w-0">
    <Text
      weight={isLeaf ? 'medium' : 'semibold'}
      className="line-clamp-2 max-w-full [overflow-wrap:anywhere]"
    >
      {label}
    </Text>
    {sublabel && (
      <Text size="xs" color="tertiary" className="max-w-full truncate">
        {sublabel}
      </Text>
    )}
  </Stack>
);

/**
 * Expandable adoption breakdown with virtualized rows (the provider level can
 * run to ~1000 rows) and a pinned first column so the row identity stays
 * visible while the bucket columns scroll horizontally.
 *
 * Rows virtualize against the page's own scroll container (via scrollMargin)
 * rather than a nested one, so the summary cards and toolbar above scroll
 * away naturally and the page keeps a single vertical scroller. The column
 * header is a separate sticky strip whose scrollLeft is slaved to the body's.
 */
export const AdoptionTable: React.FC<{
  rows: WeeklyAdoptionLevelRow[];
  bucketStarts: string[];
  levels: ReportingGroupLevel[];
  grain: ReportingGrain;
  firstColumnLabel: string;
  expanded: Set<string>;
  childrenByNode: Map<string, ChildrenState>;
  onToggle: (nodeId: string, depth: number, scope: ParentScope) => void;
  sort: TableSort | null;
  onSortChange: (next: TableSort | null) => void;
  /** Height of sticky content above (toolbar) that the header stacks under */
  stickyTopOffset?: number;
  cellMode?: TableCellMode;
}> = ({
  rows,
  bucketStarts,
  levels,
  grain,
  firstColumnLabel,
  expanded,
  childrenByNode,
  onToggle,
  sort,
  onSortChange,
  stickyTopOffset = 0,
  cellMode = 'adoption',
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const rowsWrapRef = useRef<HTMLDivElement>(null);
  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    // A computed overflow-y of auto is not enough: setting only overflow-x
    // (this table's own horizontal scroller) promotes the other axis from
    // visible to auto, so the walk would stop on an element whose clientHeight
    // is the full content height — the virtualizer then sees a viewport as
    // tall as the list and never culls a row.
    const scrollsVertically = (el: HTMLElement) =>
      /(auto|scroll)/.test(getComputedStyle(el).overflowY) &&
      el.scrollHeight > el.clientHeight;
    let parent = wrapperRef.current?.parentElement ?? null;
    while (parent && !scrollsVertically(parent)) {
      parent = parent.parentElement;
    }
    setScrollEl(parent ?? (document.scrollingElement as HTMLElement | null));
  }, []);

  const flatRows = useMemo(() => {
    // Client-side sort, applied independently at every tree level; '(none)'
    // rows and value-less cells (—) stay pinned last regardless of
    // direction. No sort keeps the server order (label-sorted, none-last).
    // Bucket columns order by adoption (completed / all), the top ratio.
    const sortValue = (
      row: WeeklyAdoptionLevelRow,
      bucketStart: string,
    ): { pct: number; numerator: number } | null => {
      const cell = row.cells.find((c) => c.bucketStart === bucketStart);
      if (!cell) {
        return null;
      }
      // Latency mode orders by median note wait (note count breaks ties)
      if (cellMode === 'latency') {
        const noteWait = cell.latency?.noteWait;
        return noteWait?.p50 == null
          ? null
          : { pct: noteWait.p50, numerator: noteWait.n };
      }
      if (cell.counts) {
        return cell.counts.total > 0
          ? {
              pct: Math.round(
                (cell.counts.completed / cell.counts.total) * 100,
              ),
              numerator: cell.counts.completed,
            }
          : null;
      }
      return cell.pct == null
        ? null
        : { pct: cell.pct, numerator: cell.numerator };
    };
    const sortRows = (
      input: WeeklyAdoptionLevelRow[],
    ): WeeklyAdoptionLevelRow[] => {
      if (!sort) {
        return input;
      }
      return [...input].sort((a, b) => {
        const aNone = a.key === REPORTING_NONE_KEY;
        const bNone = b.key === REPORTING_NONE_KEY;
        if (aNone !== bNone) {
          return aNone ? 1 : -1;
        }
        let cmp: number;
        if (sort.key === 'name') {
          cmp = a.label.localeCompare(b.label);
        } else {
          const av = sortValue(a, sort.key);
          const bv = sortValue(b, sort.key);
          if ((av == null) !== (bv == null)) {
            return av == null ? 1 : -1;
          }
          cmp =
            av == null || bv == null
              ? 0
              : av.pct - bv.pct || av.numerator - bv.numerator;
        }
        return sort.dir === 'asc' ? cmp : -cmp;
      });
    };
    const out: FlatRow[] = [];
    const walk = (
      row: WeeklyAdoptionLevelRow,
      depth: number,
      parent: { scope: ParentScope; id: string },
    ) => {
      const level = levels[depth]!;
      const nodeId = `${parent.id}${level}:${row.key}`;
      const isLeaf = depth === levels.length - 1;
      const isExpanded = expanded.has(nodeId);
      const childScope: ParentScope = {
        ...parent.scope,
        ...(level === 'location' ? { parentLocationId: row.key } : {}),
        ...(level === 'specialty' ? { parentSpecialty: row.key } : {}),
        ...(level === 'microphone' ? { parentMicrophone: row.key } : {}),
        ...(level === 'template' ? { parentTemplateId: row.key } : {}),
      };
      out.push({
        kind: 'data',
        row,
        depth,
        nodeId,
        isLeaf,
        isExpanded,
        childScope,
      });
      if (isLeaf || !isExpanded) {
        return;
      }
      const children = childrenByNode.get(nodeId);
      if (!children || children.status === 'loading') {
        out.push({ kind: 'loading', nodeId, depth: depth + 1 });
      } else if (children.status === 'error') {
        out.push({ kind: 'error', nodeId, depth: depth + 1 });
      } else {
        sortRows(children.rows).forEach((child) =>
          walk(child, depth + 1, { scope: childScope, id: `${nodeId}/` }),
        );
      }
    };
    sortRows(rows).forEach((row) => walk(row, 0, { scope: {}, id: '' }));
    return out;
  }, [rows, levels, expanded, childrenByNode, sort, cellMode]);

  // Distance from the scroll container's content top to the rows, so the
  // virtualizer can work in the page scroller's coordinate space. Adding
  // scrollTop back makes the value scroll-invariant, so it only needs
  // remeasuring when something RESIZES above the table (charts and summary
  // cards streaming in) — never on the virtualizer's scroll-driven renders,
  // where a layout read would land in the middle of a scroll frame.
  const [scrollMargin, setScrollMargin] = useState(0);
  useLayoutEffect(() => {
    const wrap = rowsWrapRef.current;
    if (!scrollEl || !wrap) {
      return;
    }
    const measure = () => {
      const next =
        wrap.getBoundingClientRect().top -
        scrollEl.getBoundingClientRect().top +
        scrollEl.scrollTop;
      setScrollMargin((prev) => (Math.abs(prev - next) < 1 ? prev : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(scrollEl);
    // The scroller's content box grows and shrinks with everything stacked
    // above the table, which is exactly what moves the rows within it
    if (scrollEl.firstElementChild) {
      observer.observe(scrollEl.firstElementChild);
    }
    return () => observer.disconnect();
  }, [scrollEl]);

  const virtualizer = useVirtualizer({
    count: flatRows.length,
    getScrollElement: () => scrollEl,
    estimateSize: () => ESTIMATED_ROW_PX,
    overscan: 12,
    scrollMargin,
    // Measurements must follow row identity, not position: expanding a node
    // inserts rows, and an index-keyed size cache then hands every row below
    // the previous occupant's height — rows overlap and the borders break.
    getItemKey: (index) => flatRowKey(flatRows[index]!),
  });

  const contentWidth = FIRST_COL_PX + bucketStarts.length * BUCKET_COL_PX;
  const hasExpanders = levels.length > 1;

  // Click cycles: default direction → flipped → cleared
  const handleSortClick = (key: TableSort['key']) => {
    const defaultDir: TableSort['dir'] = key === 'name' ? 'asc' : 'desc';
    if (sort?.key !== key) {
      onSortChange({ key, dir: defaultDir });
    } else if (sort.dir === defaultDir) {
      onSortChange({ key, dir: defaultDir === 'asc' ? 'desc' : 'asc' });
    } else {
      onSortChange(null);
    }
  };
  const sortIndicator = (key: TableSort['key']) =>
    sort?.key === key ? (
      <Icon name={sort.dir === 'asc' ? 'ArrowUp' : 'ArrowDown'} size="xs" />
    ) : (
      <Icon
        name="ArrowUpDown"
        size="xs"
        className="opacity-0 group-hover:opacity-60"
      />
    );

  return (
    <div ref={wrapperRef} className="w-full">
      <div
        ref={headerScrollRef}
        className="sticky z-20 w-full overflow-x-hidden rounded-t-lg border border-border-primary bg-background"
        style={{ top: stickyTopOffset }}
      >
        <div className="flex min-w-full" style={{ width: contentWidth }}>
          <button
            className="group sticky left-0 z-10 flex shrink-0 items-center gap-1 border-r bg-background px-3 py-2 text-left"
            style={{ width: FIRST_COL_PX }}
            aria-label={`Sort by ${firstColumnLabel}`}
            onClick={() => handleSortClick('name')}
          >
            <Text size="sm" color="tertiary" weight="medium">
              {firstColumnLabel}
            </Text>
            {sortIndicator('name')}
          </button>
          {bucketStarts.map((bucketStart) => {
            const label = format(
              new Date(`${bucketStart}T00:00:00`),
              grain === 'month' ? 'MMM yyyy' : 'MMM d',
            );
            return (
              <button
                key={bucketStart}
                className="group flex shrink-0 items-center gap-1 px-3 py-2 text-left"
                style={{ width: BUCKET_COL_PX }}
                aria-label={`Sort by ${label}`}
                onClick={() => handleSortClick(bucketStart)}
              >
                <Text size="sm" color="tertiary" weight="medium">
                  {label}
                </Text>
                {sortIndicator(bucketStart)}
              </button>
            );
          })}
        </div>
      </div>
      <div
        className="w-full overflow-x-auto rounded-b-lg border border-t-0 border-border-primary"
        onScroll={(event) => {
          if (headerScrollRef.current) {
            headerScrollRef.current.scrollLeft = event.currentTarget.scrollLeft;
          }
        }}
      >
        <div
          ref={rowsWrapRef}
          className="relative min-w-full"
          style={{ width: contentWidth, height: virtualizer.getTotalSize() }}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const flat = flatRows[virtualRow.index]!;
            return (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                className="absolute top-0 left-0 flex w-full border-b"
                style={{
                  transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
                }}
              >
                {flat.kind !== 'data' ? (
                  <div
                    className="sticky left-0 flex items-center gap-2 bg-background px-3 py-2"
                    style={{
                      width: FIRST_COL_PX,
                      paddingLeft: flat.depth * 24 + 36,
                    }}
                  >
                    {flat.kind === 'loading' ? (
                      <>
                        <LoadingIcon size="sm" />
                        <Text size="sm" color="tertiary">
                          Loading breakdown…
                        </Text>
                      </>
                    ) : (
                      <Text size="sm" color="tertiary">
                        Failed to load breakdown
                      </Text>
                    )}
                  </div>
                ) : (
                  <>
                    <div
                      className="sticky left-0 z-10 flex shrink-0 items-center overflow-hidden border-r bg-background px-3 py-2"
                      style={{ width: FIRST_COL_PX }}
                    >
                      <UserDetailsHover
                        name={flat.row.label}
                        details={flat.isLeaf ? flat.row.details : undefined}
                      >
                        <Stack
                          direction="horizontal"
                          gap={1.5}
                          align="center"
                          className="min-w-0"
                          style={{ paddingLeft: flat.depth * 24 }}
                        >
                          {!flat.isLeaf ? (
                            <Button
                              variant="ghost"
                              size="xs"
                              aria-label={
                                flat.isExpanded ? 'Collapse' : 'Expand'
                              }
                              onClick={() =>
                                onToggle(
                                  flat.nodeId,
                                  flat.depth,
                                  flat.childScope,
                                )
                              }
                            >
                              <Icon
                                name={
                                  flat.isExpanded
                                    ? 'ChevronDown'
                                    : 'ChevronRight'
                                }
                                size="sm"
                              />
                            </Button>
                          ) : hasExpanders ? (
                            <span className="w-6 shrink-0" />
                          ) : null}
                          <RowLabelBlock
                            label={flat.row.label}
                            sublabel={flat.row.sublabel}
                            isLeaf={flat.isLeaf}
                          />
                          {!flat.isLeaf && flat.row.userCount != null && (
                            <UserCountBadge count={flat.row.userCount} />
                          )}
                        </Stack>
                      </UserDetailsHover>
                    </div>
                    {flat.row.cells.map((cell) => (
                      <div
                        key={cell.bucketStart}
                        className="flex shrink-0 items-center px-3 py-2"
                        style={{ width: BUCKET_COL_PX }}
                      >
                        {cellMode === 'latency' ? (
                          <LatencyCell cell={cell} />
                        ) : (
                          <MetricCell cell={cell} />
                        )}
                      </div>
                    ))}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex justify-end pt-1.5">
        <Text size="xs" color="tertiary">
          {cellMode === 'latency'
            ? 'Cells: appointments measured · p50 / p99 wait from completing the appointment to the first generated note — hover for the full percentile ladder, stage breakdown and upload delays'
            : 'Cells: Signed / Completed / Scheduled appointments · Top bar Completion % (Completed / Scheduled) · Bottom bar Note signed % (Signed / Completed)'}
        </Text>
      </div>
    </div>
  );
};
